import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

/** コード実行結果（Judge0） */
export type ExecuteCodeResult = {
  statusId: number;
  statusDescription: string;
  stdout: string | null;
  stderr: string | null;
  compileOutput: string | null;
  message: string | null;
  time: number | null;
  wallTime: number | null;
  memory: number | null;
  exitCode: number | null;
  exitSignal: number | null;
  /** 実行時に使用したコマンドライン引数（半角スペース区切り。引数なしは null）（API6091 v2） */
  arguments: string | null;
};

/** 実行結果一覧（軽量。内容の長い項目は除外） */
export type CodeExecutionResultSummary = {
  sortOrder: number;
  seq: number;
  statusId: number;
  statusDescription: string;
  time: number | null;
  wallTime: number | null;
  memory: number | null;
  createdBy: string;
  createdByName: string;
  createdAt: string;
};

/** 実行結果詳細（内容込み） */
export type CodeExecutionResultDetail = {
  fileId: string;
  seq: number;
  sortOrder: number;
  detail: string;
  statusId: number;
  statusDescription: string;
  stdout: string | null;
  stderr: string | null;
  compileOutput: string | null;
  message: string | null;
  time: number | null;
  wallTime: number | null;
  memory: number | null;
  exitCode: number | null;
  exitSignal: number | null;
  /** 実行時に使用したコマンドライン引数（半角スペース区切り。引数なしは null）（API6093 v2） */
  arguments: string | null;
  createdBy: string;
  createdByName: string;
  createdAt: string;
};

export type DeleteCodeExecutionResultParams = {
  fileId: string;
  seq: number;
};

const sessionHeaders = () => ({
  headers: {
    'X-Session-Id': localStorage.getItem('session-id'),
  },
});

// --- API関数 ---

/**
 * コード実行API
 * 指定した fileId のソースコードを Judge0 で実行し結果を返却する。実行完了時は結果を保存する。
 * TLE(status.id=5) / Internal Error(status.id=13) も 200 で返る（エラーではない）。
 * @param fileId - 実行対象のファイルID（必須, 最大64文字）
 * @param args - 実行時引数の配列（任意）。各要素はサーバでトリム・空要素除外し半角スペース連結。
 *   連結後 512 文字以内（超過は 400）。未指定/空配列は引数なし実行（API6091 v2）
 */
export const executeCode = async (fileId: string, args?: string[]): Promise<ExecuteCodeResult> => {
  try {
    // arguments は任意。空配列や未指定なら送らない（サーバは引数なし実行）
    const body = args && args.length > 0 ? { fileId, arguments: args } : { fileId };
    const response = await axios.post(KanzhongBackend.EXECUTE_CODE, body, sessionHeaders());
    // API仕様ではトップレベル返却だが、result でラップされる場合にも対応する
    return response.data?.result ?? response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 実行結果一覧取得API
 * 指定した fileId の実行結果履歴を軽量な一覧として取得する。sort_order 降順。0件でも空配列。
 * @param fileId - 実行結果を取得する対象のファイルID
 */
export const listCodeExecutionResult = async (
  fileId: string,
): Promise<CodeExecutionResultSummary[]> => {
  try {
    const response = await axios.get(
      `${KanzhongBackend.LIST_CODE_EXECUTION_RESULT}/${encodeURIComponent(fileId)}/list`,
      sessionHeaders(),
    );
    return response.data.results ?? [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 実行結果取得API
 * 指定した fileId + seq（複合PK）の実行結果1件を内容込みで取得する。
 * @param fileId - 対象の実行結果のファイルID
 * @param seq - 対象の実行結果の連番
 */
export const getCodeExecutionResult = async (
  fileId: string,
  seq: number,
): Promise<CodeExecutionResultDetail> => {
  try {
    const response = await axios.get(
      `${KanzhongBackend.GET_CODE_EXECUTION_RESULT}/${encodeURIComponent(fileId)}/${seq}`,
      sessionHeaders(),
    );
    return response.data.result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 実行結果削除API
 * 指定した fileId + seq（複合PK）の実行結果1件を物理削除する。
 * @param params.fileId - 削除対象の実行結果のファイルID（必須, 最大64文字）
 * @param params.seq - 削除対象の実行結果の連番（必須）
 */
export const deleteCodeExecutionResult = async (
  params: DeleteCodeExecutionResultParams,
): Promise<void> => {
  try {
    await axios.post(KanzhongBackend.DELETE_CODE_EXECUTION_RESULT, params, sessionHeaders());
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
